import React from 'react';
import {
  Box,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { RagPipelineGroupSelector } from '../rag/RagSelects';
import {
  AI_MODEL_SELECT_OPTIONS,
  DEFAULT_AI_MODEL_VALUE,
  getAiModelLabel,
} from '../../../../support/aiModelOptions';

type Props = {
  useDrift: boolean;
  onUseDriftChange: (value: boolean) => void;
  pipelineId?: string;
  ragProfileId?: number | null;
  selectedRagProfile?: any | null;
  onRagProfileChange?: (value: number | null, profile?: any | null) => void;
  modelName?: string | null;
  onModelNameChange?: (value: string) => void;
  useMcp: boolean;
  onUseMcpChange: (value: boolean) => void;
  forceRefresh: boolean;
  onForceRefreshChange: (value: boolean) => void;
};

function valueOf(...values: any[]) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') return value;
  }

  return null;
}

function pipelineObjectForSlot(profile: any, slot?: string | null) {
  const normalizedSlot = String(slot || profile?.active_slot || '').toLowerCase();

  if (normalizedSlot === 'green') {
    return valueOf(
      profile?.green_pipeline_detail,
      profile?.green_pipeline,
      profile?.active_pipeline_detail,
      profile?.active_pipeline,
    );
  }

  if (normalizedSlot === 'blue') {
    return valueOf(
      profile?.blue_pipeline_detail,
      profile?.blue_pipeline,
      profile?.active_pipeline_detail,
      profile?.active_pipeline,
    );
  }

  return valueOf(
    profile?.active_pipeline_detail,
    profile?.active_pipeline,
    profile?.blue_pipeline_detail,
    profile?.blue_pipeline,
    profile?.green_pipeline_detail,
    profile?.green_pipeline,
  );
}

function pipelineExternalId(pipeline: any) {
  if (!pipeline) return null;

  if (typeof pipeline === 'string' || typeof pipeline === 'number') {
    return String(pipeline);
  }

  return valueOf(
    pipeline.provider_pipeline_id,
    pipeline.pipeline_id,
    pipeline.external_id,
    pipeline.gcs_bucket,
    pipeline.pubsub_topic,
    pipeline.key,
    pipeline.id,
  );
}

function profilePipelineId(profile: any) {
  if (!profile) return null;

  return valueOf(
    profile.active_pipeline_id,
    profile.active_provider_pipeline_id,
    profile.provider_pipeline_id,
    profile.pipeline_id,
    pipelineExternalId(pipelineObjectForSlot(profile, profile.active_slot)),
  );
}

function profileName(profile: any) {
  if (!profile) return null;

  return valueOf(
    profile.name,
    profile.label,
    profile.key,
    profile.display_name,
    profile.id,
  );
}

function buildPipelineChipLabel(profile: any, fallbackPipelineId?: string) {
  const selectedPipelineId = profilePipelineId(profile);

  if (selectedPipelineId) {
    return `Pipeline: ${selectedPipelineId}`;
  }

  return `Pipeline: ${fallbackPipelineId || 'profile/backend default'}`;
}

function buildProfileChipLabel(profile: any, ragProfileId?: number | null) {
  const name = profileName(profile);

  if (name) {
    return `Profile: ${name}`;
  }

  if (ragProfileId) {
    return `Profile: ${ragProfileId}`;
  }

  return 'Profile: backend default';
}

const McpChatControls: React.FC<Props> = ({
                                            useDrift,
                                            onUseDriftChange,
                                            pipelineId,
                                            ragProfileId,
                                            selectedRagProfile,
                                            onRagProfileChange,
                                            modelName,
                                            onModelNameChange,
                                            useMcp,
                                            onUseMcpChange,
                                            forceRefresh,
                                            onForceRefreshChange,
                                          }) => {
  const effectiveModelName = modelName || DEFAULT_AI_MODEL_VALUE;

  const pipelineChipLabel = React.useMemo(
    () => buildPipelineChipLabel(selectedRagProfile, pipelineId),
    [selectedRagProfile, pipelineId],
  );

  const profileChipLabel = React.useMemo(
    () => buildProfileChipLabel(selectedRagProfile, ragProfileId),
    [selectedRagProfile, ragProfileId],
  );

  const activeSlot = selectedRagProfile?.active_slot || null;
  const ingestSlot = selectedRagProfile?.ingest_slot || null;

  return (
    <Box
      mt={2}
      sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        pt: 1.5,
      }}
    >
      <Grid container spacing={1.5} alignItems="center">
        <Grid item xs={12} md={6} lg={4}>
          <RagPipelineGroupSelector
            value={ragProfileId || null}
            onChange={(value, row) => onRagProfileChange?.(value, row || null)}
            label="RAG Profile"
            disabled={!useDrift}
            helperText="Select or change the RAG profile used by this conversation."
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <TextField
            fullWidth
            select
            label="LLM Model"
            value={effectiveModelName}
            onChange={(event) => onModelNameChange?.(event.target.value)}
            helperText="Select or change the LLM used for new messages."
          >
            {AI_MODEL_SELECT_OPTIONS.map((option) => (
              <MenuItem key={String(option.value)} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={useDrift}
                  onChange={(event) => onUseDriftChange(event.target.checked)}
                />
              }
              label="DRIFT retrieval"
            />

            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={useMcp}
                  onChange={(event) => onUseMcpChange(event.target.checked)}
                />
              }
              label="MCP tools"
            />

            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={forceRefresh}
                  onChange={(event) => onForceRefreshChange(event.target.checked)}
                />
              }
              label="Force MCP refresh"
            />
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              variant="outlined"
              label={profileChipLabel}
            />

            <Chip
              size="small"
              variant="outlined"
              color={selectedRagProfile ? 'primary' : 'default'}
              label={pipelineChipLabel}
            />

            <Chip
              size="small"
              variant="outlined"
              color="secondary"
              label={`Model: ${getAiModelLabel(effectiveModelName)}`}
            />

            {activeSlot ? (
              <Chip
                size="small"
                variant="outlined"
                color="success"
                label={`Active: ${activeSlot}`}
              />
            ) : null}

            {ingestSlot ? (
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                label={`Ingest: ${ingestSlot}`}
              />
            ) : null}

            <Typography variant="caption" color="text.secondary">
              RAG profile and LLM model can be changed mid-conversation. New sends should use the selected values.
            </Typography>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default McpChatControls;